import { useEffect } from "react";
import { Button } from "@praesto/ui-kit";
import { browserForgetDeps, forgetOldHost } from "../lib/moved-host";
import { applyTheme, getTheme } from "../lib/theme";
import itLocale from "../locales/it.json";

// Renders instead of the whole app, before core and i18n exist (like BootGate),
// so it reads its copy from it.json.
const copy = itLocale as Readonly<Record<string, string>>;

const openInBrowser = (url: string) => window.location.assign(url);
const forgetInBrowser = () => forgetOldHost(browserForgetDeps());

/**
 * Shown on a host the app moved away from (see lib/moved-host.ts): says where
 * Praesto is now and opens the same page there. On mount it drops the old
 * host's service worker and caches, so once the old host answers with a 301 the
 * browser follows it instead of starting a cached bundle.
 */
export function MovedNotice({
  target,
  onOpen = openInBrowser,
  forget = forgetInBrowser,
}: {
  target: string;
  onOpen?: (url: string) => void;
  forget?: () => Promise<void>;
}) {
  useEffect(() => {
    applyTheme(getTheme());
    void forget();
  }, [forget]);

  return (
    <div className="min-h-screen bg-canvas text-fg-default flex items-center justify-center px-6">
      <div role="alert" className="max-w-md text-center">
        <div className="text-eyebrow flex items-center justify-center gap-2">
          <span>Praesto</span>
        </div>
        <div className="mt-6 space-y-4">
          <p className="text-base leading-relaxed">{copy["moved.title"]}</p>
          <p className="text-sm leading-relaxed text-fg-muted">{copy["moved.body"]}</p>
          <p className="text-sm leading-relaxed text-fg-muted">{copy["moved.installed"]}</p>
          <Button variant="primary" onClick={() => onOpen(target)}>
            {copy["moved.open"]}
          </Button>
          <p className="text-caption text-fg-muted">
            {copy["moved.contact"]}
            <br />
            {copy["moved.signature"]}
          </p>
        </div>
      </div>
    </div>
  );
}
